import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { PayloadStates } from '@lore/utils';
import Loader from '@/components/Loader';
import useTags from '@/hooks/useTags';
import TagTypes from '@/constants/TagTypes';
import useTagTrees from '@/hooks/useTagTrees';
import DisclosureFilter from '@/forms/_fields_v2/DisclosureFilter';
import LabelDivider from './LabelDivider';
import TagCheckboxCards from './TagCheckboxCards';

ExpertiseSelector.propTypes = {
  tags: PropTypes.object.isRequired,
  facadeTagNodes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onUpdateDescription: PropTypes.func.isRequired,
};

export default function ExpertiseSelector(props) {
  const {
    tags,
    facadeTagNodes,
    onChange,
    onUpdateDescription,
  } = props;

  const expertiseTypes = useTags(TagTypes.ExpertiseType);
  const tagTrees = useTagTrees();

  const tagsById = useMemo(() => _.keyBy(tags.data ?? [], 'id'), [tags.data]);
  const expertiseTypesById = useMemo(() => _.keyBy(expertiseTypes.data ?? [], 'id'), [expertiseTypes.data]);

  const sortedCategories = useMemo(() => {
    const expertiseByParentId = {};
    (tagTrees.data ?? []).forEach((tree) => {
      const childTag = tagsById[tree.data.childId];
      const parentTag = expertiseTypesById[tree.data.parentId];
      if (!childTag || !parentTag) return;
      if (!expertiseByParentId[tree.data.parentId]) {
        expertiseByParentId[tree.data.parentId] = { parent: parentTag, children: [] };
      }
      expertiseByParentId[tree.data.parentId].children.push(childTag);
    });
    _.forEach(expertiseByParentId, (group) => {
      group.children = _.sortBy(group.children, (t) => t.data.name);
    });
    return _.sortBy(_.values(expertiseByParentId), (group) => group.parent.data.name);
  }, [tagsById, expertiseTypesById, tagTrees.data]);

  const groupedByLetter = useMemo(
    () => _.groupBy(sortedCategories, (group) => group.parent.data.name[0].toUpperCase()),
    [sortedCategories]
  );

  if (
    tags.state === PayloadStates.FETCHING ||
    tagTrees.state === PayloadStates.FETCHING ||
    expertiseTypes.state === PayloadStates.FETCHING
  ) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col space-y-4">
      {_.map(groupedByLetter, (categories, letter) => (
        <React.Fragment key={letter}>
          <LabelDivider label={letter} />
          {/*<pre className="text-xs">{JSON.stringify(facadeTagNodes, null, 2)}</pre>*/}
          {categories.map(group => {
            const childIds = new Set(group.children.map(c => c.id));
            const selectedInGroup = facadeTagNodes.data.filter(n => childIds.has(n._tag.id));

            return (
              <DisclosureFilter
                key={group.parent.id}
                label={group.parent.data.name}
                value={selectedInGroup}
              >
                <div className="-mx-4">
                  <TagCheckboxCards
                    tags={{ data: group.children }}
                    facadeTagNodes={facadeTagNodes}
                    onChange={onChange}
                    onUpdateDescription={onUpdateDescription}
                  />
                </div>
              </DisclosureFilter>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}
